import { twMerge } from '@shared/themes';
import { StaticPageSectionContent } from '../StaticPageSectionContent/StaticPageSectionContent';
import { StaticPageSectionFooter } from '../StaticPageSectionFooter/StaticPageSectionFooter';
import type { StaticLookupRegistry, StaticPageSectionData } from '../types';
import { toStaticPageSectionId, toStaticPageSlug } from '../utils/staticPageIds';

type StaticPageSectionProps = {
  section: StaticPageSectionData;
  backToTopLabel?: string;
  lookupData?: StaticLookupRegistry;
  'data-testid'?: string;
};

/**
 * One titled block of JSON-driven static page content.
 *
 * Renders a heading with a stable id, then either a hierarchical numbered
 * clause list (default) or an unnumbered stack when `section.numbered` is
 * `false`. Intended for use inside {@link StaticPageSections}. `section.action`
 * renders in {@link StaticPageSectionFooter}, sharing a row with the back link.
 *
 * @param section - Title and content blocks from page JSON
 * @param backToTopLabel - Optional label for the jump-to-header link
 * @param lookupData - Route-owned datasets referenced by lookup blocks
 */
function StaticPageSection({ section, backToTopLabel, lookupData, 'data-testid': dataTestId }: StaticPageSectionProps) {
  const headingId = toStaticPageSectionId(section.title);
  const slug = toStaticPageSlug(section.title);
  const isContentNumbered = section.numbered !== false && section.contentNumbered !== false;

  return (
    <section className="flex flex-col gap-4" aria-labelledby={headingId} data-testid={dataTestId ?? `static-page-section-${slug}`}>
      <h2 id={headingId} className={twMerge('display-s', section.numbered === false && 'section-heading-unnumbered')}>
        {section.title}
      </h2>
      <StaticPageSectionContent content={section.content} isNumbered={isContentNumbered} lookupData={lookupData} />
      <StaticPageSectionFooter backToTopLabel={backToTopLabel} action={section.action} data-testid="static-page-section-footer" />
    </section>
  );
}

export { StaticPageSection, type StaticPageSectionProps };
